import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useFormik } from "formik";

import {
  resourcesBranch,
  resourcesReports,
  resourcesRoutes,
} from "../../../helpers/backend_helper";

import { getStorageUser } from "../../../helpers/api_helper";
import { ORDER_BY, PERIOD, REPORT_TYPE } from "../static";
import { getDefaultRouteId } from "../helper";

const initialValues = {
  route_id: "",
  branch_id: null,
  period: "Semana",
  init_date: dayjs().startOf("week").format("YYYY-MM-DD HH:mm"),
  end_date: dayjs().endOf("week").format("YYYY-MM-DD HH:mm"),
  period_range: new Date(),
  report_type: REPORT_TYPE.UNIT,
  order: ORDER_BY.y,
  time: "",
};

const useReport = ({
  unitReportName,
  operatorReportName,
  filterValues = {},
}) => {
  const authUser = getStorageUser();

  const defaultRouteId = getDefaultRouteId(authUser);

  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [branches, setBranches] = useState([]);

  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...filterValues,
      route_id: defaultRouteId,
    },
  });

  const isUnitReport = validation.values.report_type === REPORT_TYPE.UNIT;
  const reportName = isUnitReport ? unitReportName : operatorReportName;

  const { values: filters } = validation;

  const updateDateRanges = () => {
    const { period, period_range } = filters;

    let initDate = dayjs(period_range);
    let endDate = dayjs(period_range);

    switch (period) {
      case PERIOD.DAY:
        initDate = initDate.startOf("day");
        endDate = endDate.endOf("day");
        break;
      case PERIOD.WEEK:
        initDate = initDate.startOf("week");
        endDate = endDate.endOf("week");
        break;
      case PERIOD.MONTH:
        initDate = initDate.startOf("month");
        endDate = endDate.endOf("month");
        break;
    }

    const initDateStr = initDate.format("YYYY-MM-DD HH:mm");
    const endDateStr = endDate.format("YYYY-MM-DD HH:mm");

    validation.setFieldValue("init_date", initDateStr);
    validation.setFieldValue("end_date", endDateStr);
  };

  const fetchRoutes = () => {
    setIsLoadingRoutes(true);

    resourcesRoutes("get")
      .then((resp) => {
        setRoutes(resp.data);
      })
      .finally(() => {
        setIsLoadingRoutes(false);
      });
  };

  const fetchBranches = () => {
    if (!validation.values.route_id) return;

    setIsLoadingBranches(true);

    const config = {
      params: {
        route_id: validation.values.route_id,
      },
    };

    resourcesBranch("get", config)
      .then((resp) => {
        setBranches(resp.data);
      })
      .finally(() => {
        setIsLoadingBranches(false);
      });
  };

  const fetchReport = () => {
    if (!filters.route_id) return;

    const config = { params: filters };

    setIsLoadingReport(true);
    resourcesReports("show", config, reportName).then((response) => {
      setItems(response.data);
      setIsLoadingReport(false);
    });
  };

  useEffect(fetchRoutes, []);
  useEffect(fetchBranches, [filters.route_id]);
  useEffect(fetchReport, [
    filters.route_id,
    filters.branch_id,
    filters.init_date,
    filters.end_date,
    filters.report_type,
    filters.order,
    filters.time,
  ]);
  useEffect(updateDateRanges, [filters.period, filters.period_range]);

  return {
    items,
    routes,
    filters,
    branches,
    validation,
    reportName,
    isUnitReport,
    isLoadingReport,
    isLoadingRoutes,
    isLoadingBranches,
  };
};

export default useReport;
